import { useFlag } from '@afterpaytouch/feature'
import { useAppRouter, useCheckoutState, useCrossBorderTrade, useIsAfterpayAppGiftCardFlow } from '..'
import { Features } from '../../model/features'
import {
  GiftCardPromoBreakdown,
  GiftCardPromoTrackingProperties,
  getGiftCardPromoPercentOff,
  getGiftCardPromoTrackingProperties,
  isGiftCardPromoUnavailable,
  isSameMoneyAmount,
  parseGiftCardPromoBreakdown,
} from './parseGiftCardPromoBreakdown'

export type { GiftCardPromoBreakdown, GiftCardPromoTrackingProperties } from './parseGiftCardPromoBreakdown'

const MAX_PERCENT_OFF = 100
const PERCENT_OFF_FRACTION_DIGITS = 2

export const useGiftCardPromoBreakdown = (): GiftCardPromoBreakdown | null => {
  const { checkout, started } = useCheckoutState()
  const isGiftCardFlow = useIsAfterpayAppGiftCardFlow()
  const promotionUiEnabled = useFlag<boolean>(Features.CHECKOUT_GIFT_CARD_PROMOTION_UI_ENABLED, false)
  const { isCrossBorder } = useCrossBorderTrade()
  const displayedCurrency = started?.consumerAmount?.currency

  if (
    !isGiftCardFlow ||
    promotionUiEnabled !== true ||
    (isCrossBorder && displayedCurrency === undefined) ||
    (started?.consumerAmount !== undefined && !isSameMoneyAmount(checkout?.amount, started.consumerAmount))
  ) {
    return null
  }

  return parseGiftCardPromoBreakdown(checkout?.amount, checkout?.custom, displayedCurrency)
}

export const useIsGiftCardPromoUnavailable = (): boolean => {
  const { checkout } = useCheckoutState()
  const isGiftCardFlow = useIsAfterpayAppGiftCardFlow()
  const promotionUiEnabled = useFlag<boolean>(Features.CHECKOUT_GIFT_CARD_PROMOTION_UI_ENABLED, false)

  return isGiftCardFlow && promotionUiEnabled === true && isGiftCardPromoUnavailable(checkout?.custom)
}

export const useGiftCardPromoUnavailablePercentOff = (): string | null => {
  const { checkout } = useCheckoutState()
  const { locale } = useAppRouter()
  const percentOff = getGiftCardPromoPercentOff(checkout?.custom)
  if (percentOff === null || Math.round(percentOff * 10 ** PERCENT_OFF_FRACTION_DIGITS) === 0) {
    return null
  }
  return new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: PERCENT_OFF_FRACTION_DIGITS }).format(percentOff / MAX_PERCENT_OFF)
}

export const useGiftCardPromoTrackingProperties = (): GiftCardPromoTrackingProperties => {
  const { checkout } = useCheckoutState()
  return getGiftCardPromoTrackingProperties(checkout?.custom)
}
